import ArrowForward from '@material-ui/icons/ArrowForward';
import {
  FieldResponse,
  FieldValueResponse,
  FieldValueWithVersionsResponse,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import { Button } from '../../Button';
import {
  buildInitialVersionFormValue,
  getFieldValueVersions,
  VersionFormValue,
} from './helpers';
import { HistoricalValueCard } from './HistoricalValueCard';

const HistoricalValuesInfoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  margin-top: 1rem;
  background-color: ${({ theme }) => theme.colors.builderBlue08};
  border: 1px solid ${({ theme }) => theme.colors.builderBlue16};
  border-radius: 3px;
`;

// overrides for default primary button styles, making it darker since it's rendered on non-white background
const GoToHistoryButton = styled(Button)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.builderBlue};
  background-color: transparent;

  & svg {
    margin-right: 0;
    fill: ${({ theme }) => theme.colors.builderBlue};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.builderBlue};
    background-color: ${({ theme }) => theme.colors.builderBlue12};
  }

  &:focus {
    color: ${({ theme }) => theme.colors.builderBlue};
  }
`;

const ArrowContainer = styled.div`
  margin-left: 0.5rem;
  font-size: 1.25rem;
`;

function HistoricalValueInfo({
  goToHistory,
  versionCount,
}: {
  goToHistory: () => void;
  versionCount: number;
}) {
  const otherHistoricalValuesCount = versionCount - 1;
  const isPlural = otherHistoricalValuesCount > 1;
  return (
    <HistoricalValuesInfoContainer>
      <span>
        {otherHistoricalValuesCount > 0
          ? `+ ${otherHistoricalValuesCount} historical value${
              isPlural ? 's' : ''
            }`
          : 'No additional values'}
      </span>
      <GoToHistoryButton variant="text" color="primary" onClick={goToHistory}>
        <span>Edit</span>
        <ArrowContainer>
          <ArrowForward />
        </ArrowContainer>
      </GoToHistoryButton>
    </HistoricalValuesInfoContainer>
  );
}

type ActiveHistoricalValueCardProps = {
  field: FieldResponse;
  fieldValue?: FieldValueResponse | FieldValueWithVersionsResponse;
  isReadOnly?: boolean;
  onVersionedFieldSelected: (field: FieldResponse) => void;
  organizationId: string;
  projectId: string | undefined;
};

export function ActiveHistoricalValueCard({
  field,
  fieldValue,
  isReadOnly,
  onVersionedFieldSelected,
  organizationId,
  projectId,
}: ActiveHistoricalValueCardProps) {
  const arrayName = field.id;

  const originalVersions = useMemo(
    () => getFieldValueVersions(fieldValue),
    [fieldValue],
  );
  // Seed the whole version history into the form so that saving from this page
  //  (without ever opening the history list) still persists every version
  const initialVersions = useMemo(
    () =>
      originalVersions.map((version) =>
        buildInitialVersionFormValue(field, version),
      ),
    [field, originalVersions],
  );

  return (
    <FieldArray<VersionFormValue>
      name={arrayName}
      initialValue={initialVersions}
    >
      {({ fields: formFields }) => {
        const versions = formFields.value ?? [];
        const versionCount = versions.length;
        const latestIndex = Math.max(versionCount - 1, 0);
        const previousVersion = versions[latestIndex - 1];
        return (
          <HistoricalValueCard
            additionalContent={
              <HistoricalValueInfo
                goToHistory={() => onVersionedFieldSelected(field)}
                versionCount={versionCount}
              />
            }
            field={field}
            formFieldName={`${arrayName}[${latestIndex}]`}
            isFirstInHistory={versionCount === 1}
            isLastInHistory
            isReadOnly={isReadOnly}
            previousStartDate={previousVersion?.begin ?? null}
            organizationId={organizationId}
            projectId={projectId}
          />
        );
      }}
    </FieldArray>
  );
}
